import { useCallback, useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Modal, ServiceError, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { TOTPErrorCode, useEnrollTOTP, useVerifyTOTP } from "../../hooks/useTwoFactor";
import { ModalClosedAction, ModalShownAction } from "../../lib/TrackingAdapter";
import { GenericErrorModal } from "../Modal/Modal";

import style from "./SetupTwoFactorModal.module.css";

export type SetupTwoFactorModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (recoveryCode: string) => void;
};

export default function SetupTwoFactorModal({
  open,
  onClose,
  onSuccess,
}: SetupTwoFactorModalProps) {
  const { t, m } = useTranslation();

  const verificationCodeSchema = useMemo(
    () =>
      z.object({
        code: z.string().length(6, t(m.TwoFactorAuth.Setup.CodeMustBeSixDigits)),
      }),
    [t, m]
  );

  type VerificationFormData = z.infer<typeof verificationCodeSchema>;
  const tracker = useTracker();
  const {
    data: totpEnrollData,
    isPending: isEnrollTOTPPending,
    isError: isEnrollTOTPError,
    refetch: refetchEnrollTOTP,
  } = useEnrollTOTP({ enabled: open });

  const { mutateAsync: mutateVerifyTOTP } = useVerifyTOTP({
    onError: (error) => {
      let errorMessage = t(m.TwoFactorAuth.Setup.ErrorSetupAgain);

      if (error instanceof ServiceError) {
        const errorCode = error.serviceErrorResponse.code;
        if (errorCode === TOTPErrorCode.InvalidCode) {
          errorMessage = t(m.TwoFactorAuth.Setup.InvalidCode);
        } else if (errorCode === TOTPErrorCode.AlreadyEnabled) {
          errorMessage = t(m.TwoFactorAuth.Setup.AlreadyEnabled);
        } else if (errorCode === TOTPErrorCode.DeviceNotFound) {
          errorMessage = t(m.TwoFactorAuth.Setup.ErrorSetupAgain);
        } else if (errorCode === TOTPErrorCode.DeviceIdMismatch) {
          errorMessage = t(m.TwoFactorAuth.Setup.ErrorSetupAgain);
        }
      }

      setError("code", { message: errorMessage });
    },
  });

  const {
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted, isValid },
    control,
    handleSubmit,
    reset,
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationCodeSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      code: "",
    },
  });

  useEffect(() => {
    if (open) {
      refetchEnrollTOTP();
      reset();
    }
  }, [open, refetchEnrollTOTP, reset]);

  const onSubmit = useCallback(
    async (data: VerificationFormData) => {
      if (!totpEnrollData?.totpDeviceID) return;

      try {
        await mutateVerifyTOTP({ totpDeviceID: totpEnrollData.totpDeviceID, code: data.code });
        if (totpEnrollData.recoveryCode) {
          onSuccess(totpEnrollData.recoveryCode);
        }
      } catch (error) {
        // Error is handled by useEffect
      }
    },
    [totpEnrollData, mutateVerifyTOTP, onSuccess]
  );

  const handleVerificationCodeComplete = useCallback(async () => {
    await handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleCopySecret = useCallback(async () => {
    if (totpEnrollData?.key) {
      try {
        await navigator.clipboard.writeText(totpEnrollData.key);
        toast.success(t(m.TwoFactorAuth.Setup.SecretCopied));
      } catch (error) {
        toast.error(t(m.TwoFactorAuth.Setup.SecretCopyFailed));
      }
    }
  }, [totpEnrollData, t, m]);

  const isLoading = isEnrollTOTPPending;

  if (isEnrollTOTPError) {
    return <GenericErrorModal open={open} onClose={onClose} />;
  }

  return (
    <Modal
      isLoading={isLoading}
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.TWO_FACTOR_ENABLE_INTENT)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.TWO_FACTOR_ENABLE_INTENT);
        onClose();
      }}
      size="medium"
      title={t(m.TwoFactorAuth.Setup.Title)}
      footer={
        <div className={style.footer}>
          <Button variant="grayscale" onClick={onClose}>
            {t(m.TwoFactorAuth.Setup.Cancel)}
          </Button>
          <Button
            loading={isSubmitting}
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || (!isValid && isSubmitted)}
          >
            {t(m.TwoFactorAuth.Setup.Enable2FA)}
          </Button>
        </div>
      }
    >
      <div className={style.content}>
        <p className={style.description}>{t(m.TwoFactorAuth.Setup.Description)}</p>

        <div className={style.section}>
          <span className={style.sectionTitle}>{t(m.TwoFactorAuth.Setup.Step1Title)}</span>
          <p className={style.sectionDescription}>{t(m.TwoFactorAuth.Setup.Step1Description)}</p>

          <div className={style.qrCodeContainer}>
            <img
              src={`data:image/png;base64,${totpEnrollData?.verificationImage}`}
              alt="QR Code"
              className={style.qrCode}
            />
            <div className={style.secretKeyContainer}>
              <span className={style.secretKey}>{totpEnrollData?.key}</span>
              <Button
                className={style.copySecretButton}
                variant="grayscale"
                onClick={handleCopySecret}
              >
                {t(m.TwoFactorAuth.Setup.CopyToClipboard)}
              </Button>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <span className={style.sectionTitle}>{t(m.TwoFactorAuth.Setup.Step2Title)}</span>
          <p className={style.sectionDescription}>{t(m.TwoFactorAuth.Setup.Step2Description)}</p>

          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TOTPCodeInput
                className={style.codeInput}
                onChange={(value) => {
                  field.onChange(value);
                  clearErrors("code");
                }}
                onComplete={handleVerificationCodeComplete}
                error={Boolean(errors.code)}
                errorMessage={errors.code?.message}
                disabled={isLoading || isSubmitting}
                autoFocus
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
